/**
 * ClientSignupPanel — split out of AuthHub.jsx in 2026-02.
 */
import { useState } from "react";

import { toast } from "sonner";
import { User, EnvelopeSimple, ArrowLeft } from "@phosphor-icons/react";
import { clientSignup, formatUserApiError, acceptTerms } from "../../lib/userApi";

import {
  GLASS, FieldLabel, TextInput, PasswordInput, DisplayField,
  GoldButton, GhostBackButton, useAuthShellBackground, parseCardDate,
} from "./_shared";

export default function ClientSignupPanel({ onPending, onBack }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) {
      toast.error("الرجاء كتابة اسمك الكامل");
      return;
    }
    if (form.password.length < 8) {
      toast.error("كلمة المرور يجب أن تكون 8 أحرف على الأقل");
      return;
    }
    if (form.password !== form.confirm) {
      toast.error("كلمتا المرور غير متطابقتين");
      return;
    }
    setSubmitting(true);
    try {
      const normalisedEmail = form.email.trim().toLowerCase();
      // Consent gate — replay the unified acceptance to obtain the signup
      // token (signup endpoint refuses the request without it).
      const consent = await acceptTerms(normalisedEmail);
      const res = await clientSignup({
        name: form.name.trim(),
        email: normalisedEmail,
        password: form.password,
        terms_token: consent.terms_token,
      });
      toast.success("أرسلنا رمز التحقّق إلى بريدك");
      onPending({ email: res.email, role: "client", name: form.name });
    } catch (err) {
      toast.error(formatUserApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <GhostBackButton onClick={onBack} />
      <section className={`${GLASS} p-5 sm:p-6`} data-testid="client-signup-panel">
        <h1 className="text-[22px] font-extrabold text-[#0A192F] leading-tight">إنشاء حساب زائر</h1>
        <p className="text-[12.5px] text-[#5A6470] mt-1 mb-5">
          أدخِل اسمك وبريدك وكلمة المرور — وسنُرسل لك رمز تحقّق صالح لـ 10 دقائق.
        </p>
        <form onSubmit={onSubmit} className="space-y-3.5">
          <div>
            <FieldLabel>الاسم الكامل *</FieldLabel>
            <TextInput
              data-testid="client-signup-name"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              required
              minLength={2}
              placeholder="مثال: ياسر علي حسن"
              icon={User}
            />
          </div>
          <div>
            <FieldLabel>البريد الإلكتروني *</FieldLabel>
            <TextInput
              data-testid="client-signup-email"
              type="email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              required
              autoComplete="email"
              ltr
              placeholder="name@example.com"
              icon={EnvelopeSimple}
            />
          </div>
          <div>
            <FieldLabel>كلمة المرور * (8 أحرف على الأقل)</FieldLabel>
            <PasswordInput
              data-testid="client-signup-password"
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              show={show}
              onToggle={() => setShow((v) => !v)}
            />
          </div>
          <div>
            <FieldLabel>تأكيد كلمة المرور *</FieldLabel>
            <PasswordInput
              data-testid="client-signup-password-confirm"
              value={form.confirm}
              onChange={(e) => set("confirm", e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              show={show}
              onToggle={() => setShow((v) => !v)}
            />
          </div>
          <div className="pt-2">
            <GoldButton type="submit" disabled={submitting} testid="client-signup-submit">
              {submitting ? "جارٍ الإرسال…" : (<><span>إرسال رمز التحقّق</span><ArrowLeft size={18} weight="bold" /></>)}
            </GoldButton>
          </div>
        </form>
      </section>
    </>
  );
}

